import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Responsive grid with automatic mobile-first breakpoint cascade.
 *
 * cols → breakpoint mapping:
 *   1 → single column (always)
 *   2 → 1 col → sm:2
 *   3 → 1 col → sm:2 → md:3
 *   4 → 1 col → sm:2 → md:3 → lg:4
 *   5 → 2 col → sm:3 → md:5
 *
 * NEVER use raw `grid-cols-N` without breakpoints. Use this component instead.
 */

const colsMap: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3',
  4: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-5',
};

const gapMap: Record<number, string> = {
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  6: 'gap-6',
  8: 'gap-8',
};

interface ResponsiveGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of columns at largest breakpoint (1–5) */
  cols: 1 | 2 | 3 | 4 | 5;
  /** Tailwind gap unit (default: 4) */
  gap?: 2 | 3 | 4 | 6 | 8;
}

export function ResponsiveGrid({
  cols,
  gap = 4,
  className,
  children,
  ...props
}: ResponsiveGridProps) {
  return (
    <div
      className={cn('grid', colsMap[cols], gapMap[gap], className)}
      {...props}
    >
      {children}
    </div>
  );
}
